import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REPORT_REASONS = [
  'Harassment or bullying',
  'Inappropriate content',
  'Spam or scam',
  'Hate speech',
  'Impersonation',
  'Other',
];

export default function ReportModal({ user, userData, partner, onClose, onReported }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason) return;
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions();
      const submitReport = httpsCallable(functions, 'submitReport');
      const reportedId = partner?.id || userData.partnerId;

      await submitReport({
        reportedId,
        reason,
        details: details.trim(),
      });

      setStep('done');
    } catch (err) {
      setError(err.message || 'Something went wrong submitting your report.');
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        padding: '2rem',
        width: '100%',
        maxWidth: '28rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>

        {step === 'done' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1A1A1A', marginBottom: '0.5rem' }}>
              Report Submitted
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Thank you for keeping Veyl safe. You've been unmatched and will be rematched shortly.
            </p>
            <button
              onClick={onReported}
              style={{
                width: '100%', padding: '0.875rem',
                backgroundColor: '#1B7A8A', color: '#FFFFFF',
                border: 'none', borderRadius: '0.5rem',
                fontWeight: '600', cursor: 'pointer'
              }}
            >
              Find a New Partner
            </button>
          </div>

        ) : step === 'confirm' ? (
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1A1A1A', marginBottom: '0.5rem' }}>
              Are you sure?
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Reporting <strong>{partner?.name}</strong> will end your partnership immediately. This cannot be undone.
            </p>
            {error && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {error}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setStep('form')}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: '#F5F5F5', color: '#1A1A1A',
                  border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                  fontWeight: '600', cursor: 'pointer'
                }}
              >
                Go Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: '#dc2626', color: '#FFFFFF',
                  border: 'none', borderRadius: '0.5rem',
                  fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Submitting...' : 'Yes, Report'}
              </button>
            </div>
          </div>

        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1A1A1A' }}>
                Report Partner
              </h2>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            </div>

            {error && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '1rem' }}>
                {error}
              </p>
            )}

            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Select a reason for reporting <strong>{partner?.name}</strong>.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {REPORT_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  style={{
                    padding: '0.75rem 1rem',
                    border: reason === r ? '2px solid #dc2626' : '2px solid #e5e7eb',
                    backgroundColor: reason === r ? 'rgba(220,38,38,0.05)' : '#FFFFFF',
                    borderRadius: '0.5rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: reason === r ? '#dc2626' : '#1A1A1A',
                    fontWeight: reason === r ? '600' : '400',
                    fontSize: '0.875rem'
                  }}
                >
                  {r}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#1A1A1A', marginBottom: '0.5rem' }}>
                Additional details <span style={{ color: '#6b7280', fontWeight: '400' }}>(optional)</span>
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Describe what happened..."
                style={{
                  width: '100%', padding: '0.75rem',
                  border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                  height: '5rem', fontFamily: 'inherit',
                  fontSize: '0.875rem', resize: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: '#F5F5F5', color: '#1A1A1A',
                  border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                  fontWeight: '600', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={!reason}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: '#dc2626', color: '#FFFFFF',
                  border: 'none', borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: !reason ? 'not-allowed' : 'pointer',
                  opacity: !reason ? 0.5 : 1
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}